import { useEffect, useRef, useState } from 'react';
import { Check, Copy, Tag } from 'lucide-react';
import type { EventTag } from '@peles/shared';

/**
 * The event's tags, to drop into entry text as `{CODE}` (the print view and
 * the client portal fill them in). Pressing one copies it and says so —
 * a copy with no answer looked like a button that did nothing.
 */
export function TimelineTagPanel({ tags }: { tags: EventTag[] }) {
  const [copied, setCopied] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(timer.current), []);

  const copy = async (code: string) => {
    try {
      await navigator.clipboard.writeText(`{${code}}`);
      setCopied(code);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(null), 1500);
    } catch {
      // Clipboard refused (insecure origin, permissions) — the code is on screen to type.
    }
  };

  return (
    <aside
      className="bg-white rounded-xl border border-gray-200 shadow-sm p-3"
      aria-label="Event tags"
      data-testid="tag-sidebar"
    >
      <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
        <Tag size={12} aria-hidden />
        Event tags
      </div>
      <p className="text-xs text-gray-400 mb-3">
        Press one to copy it, then paste it into any entry — it is filled in when printed.
      </p>
      <ul className="space-y-1">
        {tags.map((tag) => (
          <li key={tag.id} data-testid={`sidebar-tag-${tag.code}`}>
            <button
              type="button"
              onClick={() => void copy(tag.code)}
              className="group w-full text-left rounded-md px-2 py-1.5 hover:bg-gray-50"
              title={`Copy {${tag.code}}`}
            >
              <span className="flex items-center gap-1.5">
                <code className="text-xs font-mono bg-primary-50 text-primary-700 px-1.5 py-0.5 rounded group-hover:bg-primary-100">
                  {`{${tag.code}}`}
                </code>
                {copied === tag.code ? (
                  <span className="flex items-center gap-0.5 text-xs text-green-700">
                    <Check size={12} aria-hidden /> Copied
                  </span>
                ) : (
                  <Copy
                    size={12}
                    className="text-gray-300 opacity-0 group-hover:opacity-100"
                    aria-hidden
                  />
                )}
              </span>
              <span className="block text-xs text-gray-500 mt-0.5 truncate">{tag.description}</span>
              {tag.value && (
                <span className="block text-xs text-gray-400 truncate">{tag.value}</span>
              )}
            </button>
          </li>
        ))}
      </ul>
    </aside>
  );
}
